import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/ContactsService';
import { Contact } from '../types';

export const useContact = (contactId?: string) => {
    const queryClient = useQueryClient();

    const contactQuery = useQuery({
        queryKey: ['contact', contactId],
        queryFn: () => {
            if (!contactId) throw new Error('contactId is required');
            return fetch(contactId);
        },
        enabled: !!contactId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: Contact) => {
            return add(values);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            queryClient.invalidateQueries({ queryKey: ['contact-by-email'] });
        }
    });

    const updateMutation = useMutation({
        mutationFn: async (record: Contact) => {
            if (!contactId) throw new Error('contactId is required');
            return update(contactId, record);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!contactId) throw new Error('contactId is required');
            return deleteRecord(contactId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
        }
    });

    const fetchRecord = () => {
        contactQuery.refetch();
    };

    const createRecord = (values: Contact) => {
        createMutation.mutate(values);
    };

    const updateRecord = (values: Contact) => {
        updateMutation.mutate(values);
    };

    const deleteRecordAction = () => {
        deleteMutation.mutate();
    };

    return {
        record: contactQuery.data?.data,
        loading: contactQuery.isLoading,
        fetchRecord,
        createRecord,
        updateRecord,
        deleteRecord: deleteRecordAction
    };
};
